import { useEffect, useState } from "react"

// Sumber konten publik: JSON hasil export batch-service di S3/CloudFront.
// Format file: { updated_at, section, items: [{ id, section, title, slug, data }] }
// Kita ambil items[].data (objek asli sesuai struktur statis tiap section).
//
// Default: path RELATIF ("") → same-origin lewat CloudFront (pola sama dgn
// widget harga-komoditas yang sudah jalan: `/data/harga-komoditas`). JANGAN
// pakai endpoint S3 langsung (s3.amazonaws.com/...) — itu privat & butuh
// presigned URL. VITE_DATA_BASE_URL hanya untuk override (mis. dev lokal).
const DATA_BASE = (import.meta.env.VITE_DATA_BASE_URL || "").replace(/\/$/, "")

export type LibrarySection = "penyakit" | "vaksinasi" | "jenis-ayam" | "penipu" | "racikan"

// Cache in-memory per section supaya list & detail page tidak fetch dua kali.
const cache = new Map<LibrarySection, unknown[]>()

// Base URL absolut untuk gambar (lengkap sampai domain). Prod: origin situs
// (https://beternakayam.com). Bisa dioverride via VITE_DATA_BASE_URL.
const IMG_BASE = (import.meta.env.VITE_DATA_BASE_URL || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/$/, "")

// Normalisasi URL gambar rekursif: object key S3 mentah ("data/...") → URL
// absolut penuh ("https://domain/data/..."). URL penuh (http/https) & path
// statis ("/images/..") dibiarkan. Menutup kasus JSON lama yang ter-sync
// sebelum exporter me-rewrite key → URL, tanpa perlu re-sync.
function normalizeImages(v: unknown): unknown {
  if (typeof v === "string") {
    return v.startsWith("data/") ? `${IMG_BASE}/${v}` : v
  }
  if (Array.isArray(v)) return v.map(normalizeImages)
  if (v && typeof v === "object") {
    const o = v as Record<string, unknown>
    const out: Record<string, unknown> = {}
    for (const k in o) out[k] = normalizeImages(o[k])
    return out
  }
  return v
}

export async function fetchSection<T>(section: LibrarySection): Promise<T[] | null> {
  if (cache.has(section)) return cache.get(section) as T[]
  try {
    const res = await fetch(`${DATA_BASE}/data/exports/library/${section}.json`, { cache: "no-cache" })
    if (!res.ok) return null
    const json = await res.json()
    const items = Array.isArray(json?.items) ? json.items : []
    // Gabungkan title→name & slug→id ke dalam data: item buatan admin menyimpan
    // judul di kolom `title` (bukan di dalam `data`), jadi tanpa ini `name`/`id`
    // kosong → judul hilang & routing detail pecah.
    const data = items
      .map((it: { id?: string; title?: string; slug?: string; data?: Record<string, unknown> }) => {
        const d = it.data && typeof it.data === "object" ? it.data : {}
        const merged = {
          ...d,
          id: (d.id as string) || it.slug || it.id,
          name: (d.name as string) || it.title,
          title: (d.title as string) || it.title,
        }
        return normalizeImages(merged) as Record<string, unknown>
      })
      .filter(Boolean)
    if (data.length === 0) return null
    cache.set(section, data)
    return data as T[]
  } catch {
    return null // CORS / offline / belum publik → pakai fallback statis
  }
}

// Hook: kembalikan data statis dulu (instan), lalu override dgn JSON /data
// begitu berhasil di-fetch. Kalau fetch gagal, tetap pakai statis.
export function useLibrarySection<T>(section: LibrarySection, staticFallback: T[]): T[] {
  const [items, setItems] = useState<T[]>(staticFallback)

  useEffect(() => {
    let alive = true
    fetchSection<T>(section).then((data) => {
      if (alive && data) setItems(data)
    })
    return () => {
      alive = false
    }
  }, [section])

  return items
}
